import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarMonth,
  LocationOn,
  People,
  Person,
  Phone,
  ArrowBack,
  Visibility,
  Event as EventIcon,
  ChevronLeft,
  ChevronRight,
  Close,
} from '@mui/icons-material';
import eventsService from '../utils/eventsService';
import Loader from '../components/Loader';
import { snackbar } from '../utils/showMessage';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const fileBaseUrl = process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';

  useEffect(() => {
    loadEvent();
  }, [id]);

  const galleryLength = event?.gallery?.length || 0;
  const showPrev = (e) => {
    e?.stopPropagation();
    setSelectedIndex((idx) => (idx === null ? null : (idx - 1 + galleryLength) % galleryLength));
  };
  const showNext = (e) => {
    e?.stopPropagation();
    setSelectedIndex((idx) => (idx === null ? null : (idx + 1) % galleryLength));
  };

  useEffect(() => {
    if (selectedIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSelectedIndex(null);
      else if (e.key === 'ArrowLeft') showNext();
      else if (e.key === 'ArrowRight') showPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, galleryLength]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      const response = await eventsService.getEvent(id);
      setEvent(response.data);
    } catch (error) {
      console.error('Error loading event:', error);
      snackbar('خطا در بارگذاری جزئیات مراسم');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadge = (status) => {
    const badges = {
      upcoming: { class: 'bg-primary', text: 'آینده' },
      ongoing: { class: 'bg-success', text: 'در حال برگزاری' },
      completed: { class: 'bg-secondary', text: 'برگزار شده' },
      cancelled: { class: 'bg-danger', text: 'لغو شده' },
    };
    const badge = badges[status] || badges.upcoming;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) return <Loader />;
  if (!event) return null;

  return (
    <div className="main-container container">
      {/* Header */}
      <div className="row mb-3">
        <div className="col-12">
          <button
            className="btn btn-sm btn-outline-secondary mb-3"
            onClick={() => {
              // تب فعال را از query string بخوان و به صفحه events برگردان
              const params = new URLSearchParams(location.search);
              const tab = params.get('tab') || 'upcoming';
              navigate(`/events?tab=${tab}`);
            }}
          >
            <ArrowBack sx={{ fontSize: 18, verticalAlign: 'middle', ml: 0.5 }} />
            بازگشت
          </button>
        </div>
      </div>

      {/* Poster Image */}
      {event.posterImage && (
        <div className="row mb-3">
          <div className="col-12">
            <img
              src={`${fileBaseUrl}/uploads/documents/${event.posterImage}`}
              alt={event.title}
              className="img-fluid rounded-20 shadow-sm w-100"
              style={{ maxHeight: '400px', objectFit: 'contain' }}
            />
          </div>
        </div>
      )}

      {/* Title & Status */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h4>{event.title}</h4>
            {getStatusBadge(event.status)}
          </div>
          {event.summary && (
            <p className="text-muted">{event.summary}</p>
          )}
        </div>
      </div>

      {/* Event Info */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="d-flex flex-column gap-3">
                <div className="d-flex align-items-start">
                  <CalendarMonth sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                  <div>
                    <small className="text-muted d-block">تاریخ شروع</small>
                    <strong>{formatDate(event.eventDate)}</strong>
                  </div>
                </div>

                {event.endDate && (
                  <div className="d-flex align-items-start">
                    <CalendarMonth sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                    <div>
                      <small className="text-muted d-block">تاریخ پایان</small>
                      <strong>{formatDate(event.endDate)}</strong>
                    </div>
                  </div>
                )}

                {event.location && (
                  <div className="d-flex align-items-start">
                    <LocationOn sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                    <div>
                      <small className="text-muted d-block">مکان</small>
                      <strong>{event.location}</strong>
                      {event.address && <p className="mb-0 small text-muted mt-1">{event.address}</p>}
                    </div>
                  </div>
                )}

                {event.capacity && (
                  <div className="d-flex align-items-start">
                    <People sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                    <div>
                      <small className="text-muted d-block">ظرفیت</small>
                      <strong>{event.capacity} نفر</strong>
                      {event.registeredCount > 0 && (
                        <span className="text-muted small"> ({event.registeredCount} نفر ثبت‌نام کرده)</span>
                      )}
                    </div>
                  </div>
                )}

                {event.organizerName && (
                  <div className="d-flex align-items-start">
                    <Person sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                    <div>
                      <small className="text-muted d-block">برگزارکننده</small>
                      <strong>{event.organizerName}</strong>
                    </div>
                  </div>
                )}

                {event.organizerContact && (
                  <div className="d-flex align-items-start">
                    <Phone sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                    <div>
                      <small className="text-muted d-block">تماس</small>
                      <strong>{event.organizerContact}</strong>
                    </div>
                  </div>
                )}

                <div className="d-flex align-items-start">
                  <Visibility sx={{ fontSize: 20, mr: 1, mt: 0.5 }} className="text-primary" />
                  <div>
                    <small className="text-muted d-block">بازدید</small>
                    <strong>{event.viewCount || 0} بار</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body">
              <h6 className="mb-3">توضیحات</h6>
              <div style={{ whiteSpace: 'pre-wrap' }}>{event.description}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery */}
      {event.status === 'completed' && event.gallery?.length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="mb-3">
                  <EventIcon sx={{ fontSize: 20, verticalAlign: 'middle', ml: 0.5 }} />
                  گالری تصاویر مراسم
                </h6>
                <div className="row g-2">
                  {event.gallery.map((image, index) => (
                    <div key={index} className="col-6 col-md-4 col-lg-3">
                      <img
                        src={`${fileBaseUrl}/uploads/documents/${image}`}
                        alt={`تصویر ${index + 1}`}
                        className="img-fluid rounded shadow-sm"
                        style={{ cursor: 'pointer', aspectRatio: '1', objectFit: 'cover' }}
                        onClick={() => setSelectedIndex(index)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Videos */}
      {event.status === 'completed' && event.videos?.length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6 className="mb-3">
                  <EventIcon sx={{ fontSize: 20, verticalAlign: 'middle', ml: 0.5 }} />
                  ویدیوهای مراسم
                </h6>
                <div className="row g-3">
                  {event.videos.map((video, index) => (
                    <div key={index} className="col-12 col-md-6">
                      <video
                        controls
                        className="w-100 rounded shadow-sm"
                        style={{ maxHeight: '300px' }}
                      >
                        <source
                          src={`${fileBaseUrl}/uploads/documents/${video}`}
                          type="video/mp4"
                        />
                      </video>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tags */}
      {event.tags?.length > 0 && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex flex-wrap gap-2">
              {event.tags.map((tag, index) => (
                <span key={index} className="badge bg-light text-dark">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedIndex !== null && event.gallery?.[selectedIndex] && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setSelectedIndex(null)}
        >
          <button
            type="button"
            className="btn btn-light rounded-circle position-absolute"
            style={{ top: 16, left: 16, zIndex: 1060, width: 40, height: 40, padding: 0 }}
            onClick={(e) => { e.stopPropagation(); setSelectedIndex(null); }}
            aria-label="بستن"
          >
            <Close />
          </button>

          {galleryLength > 1 && (
            <>
              <button
                type="button"
                className="btn btn-light rounded-circle position-absolute top-50 translate-middle-y"
                style={{ right: 16, zIndex: 1060, width: 48, height: 48, padding: 0 }}
                onClick={showPrev}
                aria-label="قبلی"
              >
                <ChevronRight />
              </button>
              <button
                type="button"
                className="btn btn-light rounded-circle position-absolute top-50 translate-middle-y"
                style={{ left: 16, zIndex: 1060, width: 48, height: 48, padding: 0 }}
                onClick={showNext}
                aria-label="بعدی"
              >
                <ChevronLeft />
              </button>
            </>
          )}

          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`${fileBaseUrl}/uploads/documents/${event.gallery[selectedIndex]}`}
              alt={`تصویر ${selectedIndex + 1}`}
              className="img-fluid rounded"
            />
          </div>

          {galleryLength > 1 && (
            <div
              className="position-absolute start-50 translate-middle-x text-white small"
              style={{ bottom: 16, zIndex: 1060 }}
            >
              {selectedIndex + 1} / {galleryLength}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventDetail;
